import {
	ChangeDetectorRef,
	Directive,
	HostBinding,
	HostListener,
	Input,
	OnChanges,
	OnDestroy,
	SimpleChange,
} from "@angular/core";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { NavigationExtras } from "@angular/router";
import { Store } from "@ngrx/store";
import { isExternalUrl } from "@obg/ngx.global";
import { coerceBoolean, Log, LoggingFactory } from "@obg/ngx.global.evo";
import { Subscription } from "rxjs";

import { RouteAction } from "../store/route.action";
import { RouteState } from "../store/route.model";
import { RouteService } from "../store/route.service";

import { HREF_LINKS, HREF_INACTIVE } from "./route-link.const";
import { RouteLink } from "./route-link.model";
import { RouteLinkService } from "./route-link.service";
import { extractRouteLink } from "./route-link.util";

@Directive({
	selector: "a[obgRouteLink]",
})
export class RouteLinkDirective implements OnChanges, OnDestroy {
	@Input() obgRouteLink: RouteLink | string | undefined;
	@Input() obgRouteLinkOptions: NavigationExtras | undefined;
	@HostBinding("attr.href") href: SafeResourceUrl | undefined;
	@HostBinding("attr.obg-route-link") hasAttribute = "";
	@HostBinding("attr.rel") rel = "";

	@HostBinding("attr.target")
	@Input()
	// `null` value removes attribute, `undefined` keeps it empty
	get target(): string | undefined | null {
		return this._target;
	}
	set target(value: string | undefined | null) {
		this._target = value || null;
	}

	/**
	 * Should be used to disable navigation when the user clicks the item.
	 * However, navigation will still happen if it is opened in a new tab or
	 * clicked by the crawler
	 */
	@Input()
	@coerceBoolean()
	disableClick = false;

	private _target: string | null = null;
	private logger: Log;
	private resolvedRouteLink: RouteLink | undefined;
	private data$$: Subscription | undefined;

	constructor(
		private routeLinkService: RouteLinkService,
		private service: RouteService,
		private store: Store<RouteState>,
		private changeDetector: ChangeDetectorRef,
		private domSanitizer: DomSanitizer,
		loggingFactory: LoggingFactory
	) {
		this.logger = loggingFactory.get("routeLinkDirective");
	}

	@HostListener("click", ["$event"])
	onClick(event: MouseEvent) {
		if (this.disableClick) {
			event.preventDefault();
			event.stopPropagation();
			return;
		}

		if (!this.resolvedRouteLink || this.target === "_blank") {
			return;
		}

		event.preventDefault();

		if (this.resolvedRouteLink.url) {
			this.store.dispatch(RouteAction.navigateByUrl(this.resolvedRouteLink.url, this.obgRouteLinkOptions));
		} else {
			this.store.dispatch(
				RouteAction.navigate(
					this.resolvedRouteLink.route!,
					this.resolvedRouteLink.params,
					this.obgRouteLinkOptions
				)
			);
		}
	}

	ngOnChanges(changes: { obgRouteLink?: SimpleChange }) {
		if (!this.obgRouteLink || !changes.obgRouteLink) {
			if (changes.obgRouteLink?.firstChange === false) {
				this.logger.warn("ngOnChanges", "obgRouteLink input is not specified.");
			}
			return;
		}

		const routeLink = extractRouteLink(this.obgRouteLink);
		if (routeLink && !(routeLink.route || routeLink.url)) {
			this.logger.warn("ngOnChanges", "obgRouteLink requires either 'route' or 'url'.");
			return;
		}

		const { route, params } = routeLink;
		let { url } = routeLink;

		if (this.target === "_blank") {
			const relAttributes = ["noopener"];

			if (url && isExternalUrl(url)) {
				relAttributes.push("nofollow");
			}

			this.rel = relAttributes.join(" ");
		}

		if (route) {
			this.resolvedRouteLink = { route, params };

			this.data$$?.unsubscribe();

			this.data$$ = this.service
				.generateUrl$({ routeName: route, params, extras: this.obgRouteLinkOptions })
				.subscribe((generatedUrl) => {
					this.href = generatedUrl;
					this.changeDetector.markForCheck();
				});
			return;
		}

		if (!url) {
			return;
		}

		if (!isExternalUrl(url) && !HREF_LINKS.some((protocol) => url!.startsWith(protocol))) {
			url = this.routeLinkService.getExternalUrlFromRouteLink(routeLink as RouteLink);
			this.resolvedRouteLink = { url };
		}

		if (this.target === "popup") {
			url = HREF_INACTIVE;
			this.target = null;
		}

		this.href = url ? this.domSanitizer.bypassSecurityTrustResourceUrl(url) : undefined;
	}

	ngOnDestroy() {
		this.data$$?.unsubscribe();
	}
}
